import { useState, useEffect, useCallback } from 'react';
import { createFeed, editFeed, showFeed, storeFeed, updateFeed } from '@app/api/feedProductions.api';
import React from 'react';
import { Table, Input, Typography, Spin, Select, notification } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { SearchOutlined } from '@ant-design/icons';
import { BaseCard } from '@app/components/common/BaseCard/BaseCard';
import { BaseButton } from '@app/components/common/BaseButton/BaseButton';
import { useHeader } from '@app/components/header/layouts/HeaderContext';
import { Form } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FeedLineItem, FeedRequest } from '@app/models/pr_feed/FeedRequest';
import { FeedProductionResponse } from '@app/models/pr_feed/FeedResponse';

interface FeedProductionProps {
  id?: string;
  type: 'edit' | 'create' | 'view';
  pageTitle: string;
  editable: boolean;
  // eslint-disable-next-line @typescript-eslint/ban-types
  setCurrentBreadcrumName: Function;
}

interface Feed {
  id: number;
  type?: string;
  parent_id?: number;
}

interface SubCategory {
  id: number;
  type: string;
  parent_id?: number;
  children?: Feed[];
}

interface Category {
  id: number;
  type: string;
  children?: SubCategory[];
}

const { Title } = Typography;
const { Option } = Select;

const FeedTable: React.FC<FeedProductionProps> = ({ type, pageTitle, editable, id }) => {
  const [feedTypes, setFeedTypes] = useState<Category[]>([]);
  const [quantities, setQuantities] = useState<{ [key: number]: string }>({});
  const [changedQuantities, setChangedQuantities] = useState<{ [key: number]: string }>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [form] = Form.useForm();
  const { addHeaderButton, removeHeaderButton } = useHeader();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const years = Array.from({ length: 26 }, (_, i) => new Date().getFullYear() - i);

  const fetchFeeds = useCallback(async () => {
    setLoading(true);
    try {
      let data: FeedProductionResponse;
      switch (type) {
        case 'create':
          data = await createFeed();
          if (data.feed_types) {
            setFeedTypes(data.feed_types);
          }
          break;
        case 'edit':
        case 'view':
          if (id) {
            data = type === 'edit' ? await editFeed(parseInt(id)) : await showFeed(parseInt(id));

            if (data.feed_types) {
              setFeedTypes(data.feed_types);
            }

            if (data.feed_production?.pr_feed_production_lines) {
              const existingQuantities: { [key: number]: string } = {};
              data.feed_production.pr_feed_production_lines.forEach((line) => {
                existingQuantities[line.pr_feed_type_id] = parseFloat(line.qty).toString();
              });
              setQuantities(existingQuantities);
            }

            if (data.feed_production?.year) {
              setSelectedYear(parseInt(data.feed_production.year));
            }
          }
          break;
      }
    } catch (error) {
      console.error(`Error fetching feeds for ${type} mode:`, error);
    } finally {
      setLoading(false);
    }
  }, [type, id]);

  useEffect(() => {
    fetchFeeds();
  }, [fetchFeeds]);

  const handleSubmit = async () => {
    try {
      // Prepare the feed production lines with only changed quantities
      const feedLines: FeedLineItem[] = Object.entries(changedQuantities).map(([id, qty]) => ({
        pr_feed_type_id: parseInt(id),
        qty: parseFloat(qty),
      }));

      const feedRequest: FeedRequest = {
        year: selectedYear,
        pr_feed_production_lines: feedLines,
      };

      if (type === 'create') {
        await storeFeed(feedRequest);
      } else if (type === 'edit' && id) {
        await updateFeed(parseInt(id), feedRequest);
      }
      //notification.error({ message: 'Successfully Created!' });
      navigate('/feed-productions');
    } catch (error) {
      console.error('Error submitting feed data:', error);
    }
  };

  const handleQuantityChange = (id: number, value: string) => {
    setQuantities((prev) => ({ ...prev, [id]: value }));

    if (value !== '') {
      setChangedQuantities((prev) => ({ ...prev, [id]: value }));
    } else {
      // Remove the quantity if it's empty
      const { [id]: removed, ...rest } = changedQuantities;
      setChangedQuantities(rest);
    }
  };

  useEffect(() => {
    if (editable) {
      addHeaderButton(
        'save-button',
        <BaseButton type="primary" htmlType="submit" loading={loading} onClick={handleSubmit} size="small">
          {t('Save')}
        </BaseButton>,
      );

      addHeaderButton(
        'cancel-button',
        <BaseButton onClick={() => navigate('/feed-productions')} style={{ marginRight: '8px' }} size="small">
          {t('Cancel')}
        </BaseButton>,
      );
    }

    if (!editable && id) {
      addHeaderButton(
        'edit-button',
        <BaseButton type="primary" onClick={() => navigate(`/edit-feed-production`, { state: { id } })} size="small">
          {t('Edit')}
        </BaseButton>,
      );
    }

    return () => {
      removeHeaderButton('save-button');
      removeHeaderButton('cancel-button');
      removeHeaderButton('edit-button');
    };
  }, [form, loading, editable, id, addHeaderButton, t, navigate, removeHeaderButton, changedQuantities]);

  const filterData = (categories: Category[]) => {
    if (!searchTerm) return categories;

    return categories.filter((category) => {
      // Check if category matches search term
      if (category.type.toLowerCase().includes(searchTerm.toLowerCase())) {
        return true;
      }

      // Check if any subcategory matches search term
      const matchingSubCategories = category.children?.filter((subCategory) => {
        if (subCategory.type.toLowerCase().includes(searchTerm.toLowerCase())) {
          return true;
        }

        // Check if any feed type matches search term
        const matchingFeeds = subCategory.children?.some((feed) =>
          feed.type?.toLowerCase().includes(searchTerm.toLowerCase()),
        );

        return matchingFeeds;
      });

      return matchingSubCategories && matchingSubCategories.length > 0;
    });
  };

  // Recursive function to flatten the nested structure into AntD Table format
  const transformData = (categories: Category[]) => {
    const filteredCategories = filterData(categories);

    return filteredCategories.flatMap((category) => {
      const rows: any[] = [
        {
          key: `category-${category.id}`,
          category: category.type,
          type_of_feed: null,
          isParent: true,
          children: category.children?.flatMap((subCategory) => [
            {
              key: `subcategory-${subCategory.id}`,
              category: subCategory.type,
              type_of_feed: null,
              isSubcategory: true,
              children: subCategory.children?.map((feed) => ({
                key: `feed-${feed.id}`,
                category: null,
                type_of_feed: feed.type,
                quantity: (
                  <Input
                    type="number"
                    value={quantities[feed.id] || ''}
                    onChange={(e) => handleQuantityChange(feed.id, e.target.value)}
                    style={{ width: '100px' }}
                    placeholder="Quantity"
                    disabled={!editable}
                  />
                ),
              })),
            },
          ]),
        },
      ];
      return rows;
    });
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (text, record) =>
        record.isParent ? (
          <strong style={{ fontSize: '16px' }}>{text}</strong>
        ) : record.isSubcategory ? (
          <span style={{ paddingLeft: '20px' }}>{text}</span>
        ) : null,
    },
    {
      title: 'Type of Feed',
      dataIndex: 'type_of_feed',
      key: 'type_of_feed',
      render: (text) => (text ? <span style={{ paddingLeft: '40px' }}>{text}</span> : null),
    },
    {
      title: 'Quantity (MT)',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'center',
    },
  ];

  return (
    <BaseCard>
      <Title level={3}>{pageTitle || 'Feed Types'}</Title>
      <div style={{ display: 'flex', marginBottom: '16px', gap: '10px' }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search feed types..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ maxWidth: '300px' }}
        />
        <Select
          style={{ width: '120px' }}
          value={selectedYear}
          onChange={(value) => setSelectedYear(value)}
          disabled={type !== 'create'}
        >
          {years.map((year) => (
            <Option key={year} value={year}>
              {year}
            </Option>
          ))}
        </Select>
      </div>
      {loading ? (
        <Spin size="small" />
      ) : (
        <Table
          columns={columns}
          dataSource={transformData(feedTypes)}
          pagination={false}
          bordered
          size="small"
          locale={{ emptyText: 'No data available' }}
        />
      )}
    </BaseCard>
  );
};

export default FeedTable;
